import { BaseListSubheader, BaseListItem } from './components';

const defaultGetGroupLabel = ({ name, children }) =>
  `${name} (${children.length})`;
const defaultGetOptionLabel = ({ name }) => name;
const defaultGetPrimaryTooltip = ({ name }) => name;

export const defaultGetOptionValue = ({ id }) => id;

function getParams({
  selected = false,
  indeterminate = false,
  expanded = false,
  parent = null,
  siblings = [],
}) {
  return {
    selected,
    indeterminate,
    expanded,
    parent,
    siblings,
  };
}

export const useRowRenderer = ({
  // Row
  ListItemComponent = BaseListItem,
  getOptionValue = defaultGetOptionValue,
  getOptionLabel = defaultGetOptionLabel,
  getPrimaryTooltip,
  getOptionSecondaryLabel,
  isSelected,
  isChanged,
  isDisabled,
  onItemClick,
  onItemChange,
  secondaryAction: secondaryActionComponent,
  iconComponent,
  checkbox,
  button,
  labelWrap,
  showIcon,

  // Group
  ListSubheaderComponent = BaseListSubheader,
  getGroupLabel = defaultGetGroupLabel,
  getGroupChildren,
  groupIconComponent,
  groupActionComponent,
  isGroupSelected,
  isExpanded,
  onGroupClick,
  onGroupChange,
  onExpand,
  actionCount,
  groupCheckbox,
  grouped,
}) => {
  const getRowProps = (row, parent, siblings) => {
    const value = getOptionValue?.(row);
    const primaryLabel = getOptionLabel?.(row);
    const primaryTooltip = getPrimaryTooltip?.(row);
    const secondaryLabel = getOptionSecondaryLabel?.(row);
    const secondaryAction = renderOrInsert(secondaryActionComponent, row);
    const icon = renderOrInsert(iconComponent, row);

    const selected = isSelected?.(row) || false;
    const changed = isChanged?.(row) || false;

    const handleClick = onItemClick
      ? () => onItemClick(value, row, getParams({ selected, parent, siblings }))
      : undefined;

    const disabled = isDisabled?.(value, row, getParams({ parent, siblings }));
    const depth = grouped ? 1 : 0;
    return {
      value,
      primaryLabel,
      primaryTooltip,
      secondaryLabel,
      secondaryAction,
      icon,
      selected,
      changed,
      onClick: handleClick,
      disabled,
      checkbox,
      depth,
    };
  };

  const getSubheaderProps = (row) => {
    const primaryLabel = getGroupLabel?.(row);
    const value = getOptionValue?.(row);
    const icon = renderOrInsert(groupIconComponent, row);
    const groupAction = renderOrInsert(groupActionComponent, row);
    const expanded = isExpanded(row);

    const children = getGroupChildren(row);
    const selected = children.every(isSelected);
    const indeterminate = !selected && children.some(isSelected);

    const handleChange = () => {
      onGroupChange(value, row, getParams({ selected, indeterminate }));
    };

    const handleClick = () => onGroupClick(value, row, getParams({ expanded }));

    return {
      primaryLabel,
      value,
      icon,
      groupAction,
      selected,
      indeterminate,
      expanded,
      checkbox: groupCheckbox,
      onClick: handleClick,
      onChange: handleChange,
      childrenCount: row?.children?.length,
    };
  };

  return {
    getSubheaderProps,
    renderSubheader: (row) => {
      return <ListSubheaderComponent {...getSubheaderProps(row)} />;
    },
    getRowProps,
    renderRow: (row, parent, siblings) => {
      return (
        <ListItemComponent
          {...getRowProps(row, parent, siblings)}
          divider={true}
        />
      );
    },
  };
};

function renderOrInsert(fnOrElement, props) {
  return typeof fnOrElement === 'function' ? fnOrElement(props) : fnOrElement;
}
